'use client';

import { AnimatePresence, motion } from 'motion/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useLocale } from '@/contexts/LocaleContext';
import { cn } from '@/utils/cn';
import { getDictionary } from '@/utils/getDictionary';

import styles from './SearchField.module.scss';

type Props = {
  label?: string;
  wide?: boolean;
};

export const SearchField: React.FC<Props> = (props) => {
  const { label, wide } = props;

  const router = useRouter();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const t = getDictionary(locale);

  // Set the initial search query to the query parameter in the URL
  const initialQuery = searchParams.get('query') || '';
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);

  // Debounce the search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Perform the search when the debounced query changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());

    if (debouncedQuery.trim()) {
      params.set('query', debouncedQuery.trim());
    } else {
      params.delete('query');
    }

    router.push(`?${params.toString()}`, {
      scroll: false,
    });
  }, [debouncedQuery, router, searchParams]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('query');
    router.push(`?${params.toString()}`, {
      scroll: false,
    });
    setSearchQuery('');
  };

  return (
    <form
      className={cn(styles.wrapper, wide && styles.wide)}
      onSubmit={(e) => e.preventDefault()}
    >
      <input
        className={styles.input}
        type='text'
        name='search'
        value={searchQuery}
        onChange={handleSearch}
        placeholder={label || t('search')}
      />
      <button
        className={styles.button}
        type='button'
        onClick={clearSearch}
        aria-label={t('search')}
      >
        <AnimatePresence mode='wait' initial={false}>
          {searchQuery ? (
            <motion.svg
              key='close-icon'
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              style={{ willChange: 'opacity, transform' }}
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M15.281 14.2194C15.3507 14.289 15.406 14.3718 15.4437 14.4628C15.4814 14.5539 15.5008 14.6514 15.5008 14.75C15.5008 14.8485 15.4814 14.9461 15.4437 15.0372C15.406 15.1282 15.3507 15.2109 15.281 15.2806C15.2114 15.3503 15.1286 15.4056 15.0376 15.4433C14.9465 15.481 14.849 15.5004 14.7504 15.5004C14.6519 15.5004 14.5543 15.481 14.4632 15.4433C14.3722 15.4056 14.2895 15.3503 14.2198 15.2806L8.00042 9.0603L1.78104 15.2806C1.64031 15.4213 1.44944 15.5004 1.25042 15.5004C1.05139 15.5004 0.860522 15.4213 0.719792 15.2806C0.579062 15.1399 0.5 14.949 0.5 14.75C0.5 14.551 0.579062 14.3601 0.719792 14.2194L6.9401 7.99999L0.719792 1.78061C0.579062 1.63988 0.5 1.44901 0.5 1.24999C0.5 1.05097 0.579062 0.860095 0.719792 0.719365C0.860522 0.578634 1.05139 0.499573 1.25042 0.499573C1.44944 0.499573 1.64031 0.578634 1.78104 0.719365L8.00042 6.93968L14.2198 0.719365C14.3605 0.578634 14.5514 0.499573 14.7504 0.499573C14.9494 0.499573 15.1403 0.578634 15.281 0.719365C15.4218 0.860095 15.5008 1.05097 15.5008 1.24999C15.5008 1.44901 15.4218 1.63988 15.281 1.78061L9.06073 7.99999L15.281 14.2194Z'
                fill='#1B1B1B'
              />
            </motion.svg>
          ) : (
            <motion.svg
              key='search-icon'
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              style={{ willChange: 'opacity, transform' }}
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M14.3536 13.6463L11.2243 10.5175C12.1313 9.42859 12.5836 8.03189 12.487 6.61798C12.3905 5.20407 11.7526 3.88181 10.706 2.92625C9.65938 1.9707 8.28468 1.45543 6.86784 1.48763C5.451 1.51983 4.10112 2.09702 3.09901 3.09913C2.0969 4.10125 1.51971 5.45113 1.48751 6.86796C1.45531 8.2848 1.97058 9.65951 2.92613 10.7061C3.88168 11.7527 5.20395 12.3906 6.61786 12.4872C8.03177 12.5837 9.42846 12.1314 10.5174 11.2244L13.6461 14.3538C13.6926 14.4002 13.7478 14.4371 13.8085 14.4622C13.8691 14.4874 13.9342 14.5003 13.9999 14.5003C14.0656 14.5003 14.1307 14.4874 14.1913 14.4622C14.252 14.4371 14.3072 14.4002 14.3536 14.3538C14.4001 14.3073 14.437 14.2522 14.4621 14.1915C14.4872 14.1308 14.5002 14.0657 14.5002 14C14.5002 13.9343 14.4872 13.8693 14.4621 13.8086C14.437 13.7479 14.4001 13.6927 14.3536 13.6463ZM2.4999 7.00002C2.4999 6.11 2.76382 5.23998 3.25829 4.49995C3.75275 3.75993 4.45556 3.18316 5.27782 2.84256C6.10009 2.50197 7.00489 2.41285 7.8778 2.58649C8.75072 2.76012 9.55254 3.1887 10.1819 3.81804C10.8112 4.44738 11.2398 5.2492 11.4134 6.12211C11.5871 6.99503 11.498 7.89983 11.1574 8.7221C10.8168 9.54436 10.24 10.2472 9.49996 10.7416C8.75994 11.2361 7.88991 11.5 6.9999 11.5C5.80683 11.4987 4.66301 11.0242 3.81938 10.1805C2.97575 9.33691 2.50122 8.19309 2.4999 7.00002Z'
                fill='#1B1B1B'
              />
            </motion.svg>
          )}
        </AnimatePresence>
      </button>
    </form>
  );
};
